import { useEffect, useState } from "react";
import withDragAndDrop from "react-big-calendar/lib/addons/dragAndDrop";
import { Calendar, DateLocalizer, dayjsLocalizer } from "react-big-calendar";

import dayjs from "dayjs";
import PropTypes from "prop-types";

import "@/styles/calendar.css";

import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Button } from "@/components/ui/button";
import KanbanDrawerContent from "@/routes/(planner-screens)/components/kanban/kanban-drawer-content";

import {
  Job,
  Machine,
  Technician,
} from "@/hooks/services/pps/types/job-orders.type";

// import { defineView, tranformEvent } from "@/app/(homepage)/calendar/utils";
import { showPriorityIcon } from "@/lib/utils";
import { User, Wrench } from "lucide-react";
import { defaultSelectedItem } from "@/routes/(planner-screens)/utils/constants";
import {
  defineView,
  tranformEvent,
} from "@/routes/(planner-screens)/utils/utils";

const localizer = dayjsLocalizer(dayjs);
const DragAndDropCalendar = withDragAndDrop(Calendar);

type CalendarView = "month" | "week" | "day" | "agenda";
export type stringOrDate = string | Date;

interface CalendarItem {
  id: number | null;
  title: string;
  desc: string;
  start: stringOrDate;
  end: stringOrDate;
}

interface DrawerItem {
  show: boolean;
  task: Job;
}

export default function AiCalendarComponent({ data }: { data: any }) {
  const [events, setEvents] = useState<CalendarItem[]>([]);

  const [calendarView, setCalendarView] = useState<CalendarView>("month");
  const [currentDate, setCurrentDate] = useState<Date>(new Date());
  const [selectedItem, setSelectedItem] = useState<DrawerItem>({
    show: false,
    task: defaultSelectedItem,
  });

  useEffect(() => {
    if (data) {
      // need to transform cause the react big calendar expect events to have a specific structure
      const transformBody: CalendarItem[] = data.map((item: any) =>
        tranformEvent(item),
      );

      setEvents(transformBody);
    }
  }, [data]);

  const eventStyleGetter = () => {
    let style: React.CSSProperties = {};

    if (calendarView === "agenda") {
      style = {
        backgroundColor: "hsl(var(--primary-foreground))",
        padding: "2px 5px",
        border: "none",
      };
    } else {
      style = {
        backgroundColor: "hsl(var(--primary))",
        color: "hsl(var(--primary-foreground))",
        borderRadius: "5px",
        padding: "2px 5px",
      };
    }
    return {
      style,
    };
  };

  const dayStyleGetter = () => {
    const style: React.CSSProperties = {
      color: "hsl(var(--muted-foreground))",
    };
    return { style };
  };

  const CustomToolbar = (props: any) => {
    const { label, onNavigate, views, date } = props;

    const handleNavigation = (action: string) => {
      const newDate = dayjs(date);

      if (action === "PREV") {
        newDate.subtract(1, defineView(calendarView));
      } else if (action === "NEXT") {
        newDate.add(1, defineView(calendarView));
      } else if (action === "TODAY") {
        newDate
          .set("year", newDate.year())
          .set("month", newDate.month())
          .set("date", newDate.date());
      }

      setCurrentDate(newDate.toDate());
      onNavigate(action, newDate.toDate());
    };

    return (
      <div className="aero:flex aero:justify-between aero:items-center aero:p-2 aero:mb-2">
        <div>
          <Button
            onClick={() => handleNavigation("PREV")}
            intent="custom"
            design="outline"
            className="aero:mr-2"
          >
            Back
          </Button>
          <Button
            onClick={() => {
              handleNavigation("NEXT");
            }}
            intent="custom"
            design="outline"
          >
            Next
          </Button>
        </div>
        <h2 className="aero:!text-xl aero:!font-semibold">{label}</h2>
        <div className="aero:flex aero:items-center">
          <Select
            value={calendarView}
            onValueChange={(newView) =>
              setCalendarView(newView as CalendarView)
            }
          >
            <SelectTrigger className="aero:w-[180px]">
              <SelectValue placeholder="Select view" />
            </SelectTrigger>
            <SelectContent>
              {views.map((name: string) => (
                <SelectItem key={name} value={name}>
                  {name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Button
            onClick={() => handleNavigation("TODAY")}
            intent="custom"
            design="outline"
            className="aero:ml-2"
          >
            Today
          </Button>
        </div>
      </div>
    );
  };

  const CustomEvent = ({ event }: any) => {
    const isMonthView = calendarView === "month";
    const maxMachines = isMonthView ? 1 : 2;
    const maxTechnicians = isMonthView ? 1 : 2;
    // const maxIssues = isMonthView ? 1 : 2;
    return (
      <div
        className="aero:rbc-event-content aero:p-1 aero:overflow-hidden"
        title={event.title}
      >
        <div className="aero:flex aero:flex-col aero:h-full">
          <div className="aero:flex aero:justify-between aero:items-center aero:mb-1">
            <div className="aero:!font-semibold aero:!text-sm aero:truncate aero:flex aero:items-center aero:gap-2">
              <span>{event.title}</span>
              {showPriorityIcon(event.priority)}
            </div>
            <div className="aero:!text-xs aero:opacity-75 aero:whitespace-nowrap aero:flex aero:gap-2">
              <div> {dayjs(event.dueDate).format("DD MMM YYYY")} </div>
              <span> | </span>
              <div>
                {event.standardHours.slice(0, 2)} <span>hrs</span>
              </div>
            </div>
          </div>

          {event.technicians && event.technicians.length > 0 && (
            <div className="aero:flex aero:items-center aero:mb-1">
              <User className="aero:w-3 aero:h-3 aero:mr-1" />
              <div className="aero:!text-xs aero:truncate">
                {event.technicians
                  .slice(0, maxTechnicians)
                  .map((tech: Technician) => tech.name)
                  .join(", ")}
                {event.technicians.length > maxTechnicians && "..."}
              </div>
            </div>
          )}

          {event.machines && event.machines.length > 0 && (
            <div className="aero:flex aero:items-center aero:mb-1">
              <Wrench className="aero:w-3 aero:h-3 aero:mr-1" />
              <div className="aero:!text-xs aero:truncate">
                {event.machines
                  .slice(0, maxMachines)
                  .map((machine: Machine) => machine.machineName)
                  .join(", ")}
                {event.machines.length > maxMachines && "..."}
              </div>
            </div>
          )}
        </div>
      </div>
    );
  };

  return (
    <>
      <div className="aero:flex aero:justify-center aero:items-center">
        <DragAndDropCalendar
          localizer={localizer}
          events={events}
          backgroundEvents={events}
          view={calendarView}
          date={currentDate}
          defaultView="month"
          defaultDate={dayjs().toDate()}
          eventPropGetter={eventStyleGetter}
          dayPropGetter={dayStyleGetter}
          onSelectEvent={(event) => {
            const eventItem = event as CalendarItem;

            setSelectedItem((prev) => ({
              show: !prev.show,
              task:
                (data.find(
                  (f: any) => f.id === eventItem.id,
                ) as unknown as Job) || defaultSelectedItem,
            }));
          }}
          onNavigate={(value) => {
            setCurrentDate(dayjs(value).toDate());
          }}
          onView={(value) => {
            setCalendarView(value as CalendarView);
          }}
          components={{
            toolbar: CustomToolbar,
            event: CustomEvent,
          }}
          popup
          popupOffset={{ x: 30, y: 20 }}
          resizable
          selectable
          step={60}
          timeslots={10}
          showMultiDayTimes
          style={{ height: "calc(80vh - 60px)", width: "98%" }}
          className="aero:bg-background aero:border aero:!rounded-lg aero:shadow-lg"
          showAllEvents={true}
        />
      </div>

      <KanbanDrawerContent
        open={selectedItem.show}
        onOpenChange={(open) =>
          setSelectedItem((prev) => ({
            ...prev,
            show: open,
          }))
        }
        data={selectedItem.task}
        viewMode
      />
    </>
  );
}

AiCalendarComponent.propTypes = {
  localizer: PropTypes.instanceOf(DateLocalizer),
};
